import { fromJS } from 'immutable';
import { gate } from 'hub-http/gates';
import { SUB_APPS } from 'reporting-data/tsTypes/subAppTypes';
import { ANALYTICS_SUB_SUITE_IDS } from 'reporting-discover-components/constants/AnalyticsSubSuiteIds';
import { ANALYTICS_SUITE_IDS, REVENUE_SUB_APP } from './templates';

// Gates following "gate(name-of-gate)" patterns are V3 gates
export const Gates = {
  SALES_REPORTS_EXP: 'RA:SalesExplorerExp',
  SCP_CASE_STUDIES: 'StructuredContentPlatform:CaseStudies',
  IMPROVED_RECURRING_REVENUE: gate('RA:ImprovedRecurringRevenue'),
  REPORT_SETUP: 'Reporting:ReportSetup',
  TEAM_ACTIVITY: gate('RA:TeamActivityReport'),
  ACTIVITIES_AND_LEADS_NEW_IA: gate('RA:ActivitiesAndLeadsNewIA'),
  SALES_EMAIL_RATES: gate('ConnectedEmail:CRB:DeliverabilityStatsVisibility'),
  // both needed for new calculated stage properties
  TICKET_DATE_ENTERED_PROPERTIES: gate('CRM:SCP:Ticket:Access'),
  HD_INSIGHTS_SCP: gate('hd:insights:scp'),
  // general feature gates
  UNUSED_REPORTS_FILTER: gate('RH:UnusedReports'),
  PIPELINE_SNAPSHOTS_ALERT: 'RM:Migration:HistoricalSnapshotMigration',
  REPORT_LIST_VIEW_TOGGLE: gate('RH:ReportListViewToggle'),
  SMART_LIBRARY: gate('RH:SmartLibrary'),
  LAST_VIEWED_AT_FILTER: gate('RH:LastViewedAtFilter'),
  BRANDS_MULTI_SELECT: gate('RH:BrandsMultiSelectFilter'),
  USE_CHIRP_ENDPOINTS: gate('RH:UseChirpEndpoints'),
  TAGGING: gate('RH:ReportTagging')
};
export const Scopes = {
  CONTACT_ANALYTICS: 'contact-analytics',
  PAGES_ANALYTICS: 'pages-analytics',
  REVENUE_ANALYTICS: 'revenue-analytics',
  REPORTS_SALES_PRO: 'reports-sales-pro-access',
  SERVICE_ANALYTICS: 'service-team-analytics',
  SOURCES_ANALYTICS: 'sources-analytics',
  SUBSCRIPTIONS_ACCESS: 'subscriptions-access',
  SUBSCRIPTION_TEAM_ANALYTICS: 'subscription-team-analytics',
  UTM_ANALYTICS: 'utm-analytics',
  ANALYTICS_VIEWS: 'analytics-filters-read',
  TOPICS_ANALYTICS: 'topic-clusters-reports-access',
  ATTRIBUTION_REPORTS_ACCESS: 'attribution-reports-enterprise-access',
  REVENUE_ATTRIBUTION_READ: 'revenue-attribution-read',
  CASE_STUDY_ACCESS: 'case-study-access',
  JOURNEY_ANALYTICS: 'journey-analytics-read',
  SUPER_ADMIN: 'super-admin',
  EMAIL_ACCESS: 'email-access',
  EMAIL_DASHBOARD_ACCESS: 'email-dashboard-access',
  SMS_MARKETING_ADDON: 'sms-marketing-addon',
  SMS_VIEW: 'sms-view',
  CTA_ACCESS: 'calls-to-action-read',
  HD_SERVICE_ANALYTICS: 'hd-service-analytics',
  SOCIAL_ACCESS: 'social-access',
  ADS_READ: 'ads-read',
  FORMS_READ: 'forms-read',
  SERVICE_JOURNEY_ANALYTIC_READ: 'service-journey-analytics-read',
  CUSTOM_REPORTING_ACCESS: 'custom-reporting-access',
  SUBSCRIPTIONS_READ: 'subscriptions-read',
  INVOICES_READ: 'invoices-read',
  PAYMENTS_READ: 'commerce-payments-read'
};
export const COLLECTION_ACCESS = fromJS({
  // MARKETING COLLECTION
  [ANALYTICS_SUITE_IDS.MARKETING]: {
    requireAnyScope: [Scopes.SOURCES_ANALYTICS, Scopes.PAGES_ANALYTICS, Scopes.UTM_ANALYTICS, Scopes.CONTACT_ANALYTICS],
    upgradePoint: 'marketing-reports-locked-nav-item'
  },
  // MARKETING - CONTACT INSIGHTS SECTION
  [SUB_APPS.contactInsights]: {
    requireScope: Scopes.CONTACT_ANALYTICS,
    upgradePoint: 'contact-reports-locked-nav-item'
  },
  // MARKETING - CHANNEL PERFORMANCE SECTION - MARKETING EMAIL REPORTS
  [SUB_APPS.marketingEmails]: {
    visibilityControl: {
      requireAllScopes: [Scopes.EMAIL_ACCESS, Scopes.EMAIL_DASHBOARD_ACCESS]
    }
  },
  // MARKETING - CHANNEL PERFORMANCE SECTION - SMS REPORT
  [SUB_APPS.sms]: {
    visibilityControl: {
      requireAllScopes: [Scopes.SMS_MARKETING_ADDON, Scopes.SMS_VIEW]
    }
  },
  // MARKETING - CHANNEL PERFORMANCE SECTION - SOCIAL
  [SUB_APPS.social]: {
    visibilityControl: {
      requireScope: Scopes.SOCIAL_ACCESS
    }
  },
  // MARKETING - CHANNEL PERFORMANCE SECTION - CTA REPORT
  [SUB_APPS.cta]: {
    visibilityControl: {
      requireScope: Scopes.CTA_ACCESS
    }
  },
  // MARKETING - CHANNEL PERFORMANCE SECTION - ADS REPORT
  [SUB_APPS.ads]: {
    visibilityControl: {
      requireScope: Scopes.ADS_READ
    }
  },
  // MARKETING - CHANNEL PERFORMANCE SECTION - FORMS REPORT
  [SUB_APPS.leadCapture]: {
    visibilityControl: {
      requireScope: Scopes.FORMS_READ
    }
  },
  // MARKETING - WEB TRAFFIC ANALYSIS SECTION
  [ANALYTICS_SUB_SUITE_IDS.WEB_TRAFFIC_ANALYSIS]: {
    requireAnyScope: [Scopes.SOURCES_ANALYTICS, Scopes.PAGES_ANALYTICS, Scopes.UTM_ANALYTICS],
    upgradePoint: 'website-performance-reports-locked-nav-item'
  },
  // MARKETING - WEB TRAFFIC - SOURCES REPORTS
  [SUB_APPS.sources]: {
    visibilityControl: {
      requireScope: Scopes.SOURCES_ANALYTICS
    }
  },
  // MARKETING - WEB TRAFFIC - PAGES REPORTS
  [SUB_APPS.pages]: {
    visibilityControl: {
      requireScope: Scopes.PAGES_ANALYTICS
    }
  },
  // MARKETING - WEB TRAFFIC - UTM REPORTS
  [SUB_APPS.utmParameters]: {
    visibilityControl: {
      requireScope: Scopes.UTM_ANALYTICS
    }
  },
  // MARKETING - WEB TRAFFIC - DEVICE TYPE REPORTS
  [SUB_APPS.deviceTypes]: {
    visibilityControl: {
      requireScope: Scopes.SOURCES_ANALYTICS
    }
  },
  // MARKETING - WEB TRAFFIC - COUNTRIES REPORTS
  [SUB_APPS.geolocation]: {
    visibilityControl: {
      requireScope: Scopes.SOURCES_ANALYTICS
    }
  },
  // MARKETING - WEB TRAFFIC - BROWSERS REPORTS
  [SUB_APPS.browsers]: {
    visibilityControl: {
      requireScope: Scopes.SOURCES_ANALYTICS
    }
  },
  // MARKETING - WEB TRAFFIC - TOPIC CLUSTER REPORTS
  [SUB_APPS.topicClusters]: {
    visibilityControl: {
      requireScope: Scopes.TOPICS_ANALYTICS
    }
  },
  // MARKETING - ADVANCED REPORTING SECTION
  [ANALYTICS_SUB_SUITE_IDS.ADVANCED_MARKETING_REPORTING]: {
    requireAllScopes: [Scopes.ATTRIBUTION_REPORTS_ACCESS, Scopes.JOURNEY_ANALYTICS],
    upgradePoint: 'marketing-analytics-advanced-reporting-upgrade'
  },
  // MARKETING - ADVANCED REPORTING - ATTRIBUTION
  [SUB_APPS.attribution]: {
    visibilityControl: {
      requireAllScopes: [Scopes.ATTRIBUTION_REPORTS_ACCESS, Scopes.REVENUE_ATTRIBUTION_READ]
    }
  },
  // MARKETING - ADVANCED REPORTING - CUSTOMER JOURNEY
  [SUB_APPS.customerJourneyAnalytics]: {
    requireScope: Scopes.JOURNEY_ANALYTICS
  },
  // SERVICE COLLECTION
  [ANALYTICS_SUITE_IDS.SERVICE]: {
    requireScope: Scopes.SERVICE_ANALYTICS,
    upgradePoint: 'service-reports-locked-nav-item'
  },
  [ANALYTICS_SUB_SUITE_IDS.TICKET_REPLIES]: {
    requireScope: Scopes.HD_SERVICE_ANALYTICS,
    upgradePoint: 'svh-report-collections'
  },
  [ANALYTICS_SUB_SUITE_IDS.SERVICE_PLANNING]: {
    requireScope: Scopes.HD_SERVICE_ANALYTICS,
    upgradePoint: 'svh-report-collections'
  },
  [ANALYTICS_SUB_SUITE_IDS.SELF_SERVICE_PERFORMANCE]: {
    requireScope: Scopes.HD_SERVICE_ANALYTICS,
    visibilityControl: {
      requireScope: Scopes.SERVICE_JOURNEY_ANALYTIC_READ
    },
    upgradePoint: 'service-journey-analytics'
  },
  [SUB_APPS.ticketEnteredNew]: {
    visibilityControl: {
      requireAllGates: [Gates.TICKET_DATE_ENTERED_PROPERTIES, Gates.HD_INSIGHTS_SCP]
    }
  },
  [SUB_APPS.ticketEnteredWaiting]: {
    visibilityControl: {
      requireAllGates: [Gates.TICKET_DATE_ENTERED_PROPERTIES, Gates.HD_INSIGHTS_SCP]
    }
  },
  // COMMERCE COLLECTION
  [ANALYTICS_SUITE_IDS.COMMERCE]: {
    requireScope: Scopes.SUBSCRIPTION_TEAM_ANALYTICS,
    visibilityControl: {
      requireScope: Scopes.SUBSCRIPTION_TEAM_ANALYTICS
    }
  },
  [ANALYTICS_SUB_SUITE_IDS.INVOICES]: {
    visibilityControl: {
      requireScope: Scopes.INVOICES_READ
    }
  },
  [ANALYTICS_SUB_SUITE_IDS.SUBSCRIPTIONS]: {
    visibilityControl: {
      requireScope: Scopes.SUBSCRIPTIONS_READ
    }
  },
  [ANALYTICS_SUB_SUITE_IDS.PAYMENTS]: {
    visibilityControl: {
      requireScope: Scopes.PAYMENTS_READ
    }
  },
  // SALES COLLECTION
  [ANALYTICS_SUITE_IDS.SALES]: {
    requireAnyCombination: [{
      requireScope: Scopes.REPORTS_SALES_PRO
    }, {
      requireGate: Gates.SALES_REPORTS_EXP
    }],
    upgradePoint: 'sales-performance-reports-locked-nav-item'
  },
  // SALES - REVENUE SUB SECTION
  [REVENUE_SUB_APP]: {
    visibilityControl: {
      requireScope: Scopes.REVENUE_ANALYTICS
    }
  },
  [SUB_APPS.lineItemRevenue]: {
    visibilityControl: {
      requireGate: Gates.IMPROVED_RECURRING_REVENUE,
      requireScope: Scopes.REPORTS_SALES_PRO
    }
  },
  [SUB_APPS.teamActivity]: {
    visibilityControl: {
      requireGate: Gates.TEAM_ACTIVITY
    }
  },
  [SUB_APPS.emailRates]: {
    visibilityControl: {
      requireGate: Gates.SALES_EMAIL_RATES
    }
  },
  [SUB_APPS.callOutcomes]: {
    visibilityControl: {
      forbiddenGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.prospectEngagement]: {
    visibilityControl: {
      forbiddenGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.meetingOutcomes]: {
    visibilityControl: {
      forbiddenGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.taskOutcomes]: {
    visibilityControl: {
      forbiddenGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.leadFunnel]: {
    visibilityControl: {
      forbiddenGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.leadResponseTime]: {
    visibilityControl: {
      forbiddenGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.calls]: {
    visibilityControl: {
      requireGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.tasks]: {
    visibilityControl: {
      requireGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.meetings]: {
    visibilityControl: {
      requireGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.sequences]: {
    visibilityControl: {
      requireGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  },
  [SUB_APPS.leads]: {
    visibilityControl: {
      requireGate: Gates.ACTIVITIES_AND_LEADS_NEW_IA
    }
  }
});